import { useEffect, useState } from 'react';
import { useDealerSearchQuery } from 'services/dealer-search/use-query-dealer-search';
import { DealerSearchFormData } from '../types';
import {
  CUSTOMER_NUMBER_INPUT_NAME,
  DEALER_DROPDOWN_NAME,
  DEALER_INPUT_ADDRESS,
  DEALER_INPUT_CITY,
  DEALER_INPUT_ZIP_CODE
} from '../constants/constants';
import { isEqual } from 'lodash';
import { useEsiteCtx } from 'services/esite/context';
import { useQueryClient } from '@tanstack/react-query';

export const useDealerSearch = (dealerStoreId = null) => {
  const { storeId } = useEsiteCtx();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    [DEALER_DROPDOWN_NAME]: '',
    [CUSTOMER_NUMBER_INPUT_NAME]: '',
    [DEALER_INPUT_ADDRESS]: '',
    [DEALER_INPUT_CITY]: '',
    [DEALER_INPUT_ZIP_CODE]: ''
  });

  const { data, isFetched, isError, refetch, isLoading } = useDealerSearchQuery(
    {
      data: formData,
      storeId: dealerStoreId ?? storeId
    }
  );

  // * checks if required fields are filled
  const fieldsAreFilled = (data: DealerSearchFormData) => {
    return Object.entries(data).some(
      ([key, value]) => key !== DEALER_DROPDOWN_NAME && value !== ''
    );
  };

  // remove method doest exist anymore so we have to implement removeQuereries method from queryClient
  // to have the same behavior as remove
  const handleRemoveQuery = () => {
    const queryCache = queryClient.getQueryCache();

    //Getting the active keys and looking for dealerCustomer key
    const queryKeys = queryCache.getAll().map((cache) => cache.queryKey);
    const dealerCustomerKey = queryKeys.find((Key) =>
      Key.includes('dealerSearch')
    );

    queryClient.removeQueries({ queryKey: dealerCustomerKey });
  };

  // * sets payload data
  const handleSearch = (data: DealerSearchFormData) => {
    // * remove previous query state
    handleRemoveQuery();

    // * manual refetch since useEffect doesn't run when state is the same
    const isSameState = isEqual(formData, data);
    const someFieldsAreFilled = fieldsAreFilled(data);
    if (isSameState && someFieldsAreFilled) {
      refetch();
    } else {
      setFormData(data);
    }
  };

  // * waits for payload state to be setted to refetch data
  useEffect(() => {
    const someFieldsAreFilled = fieldsAreFilled(formData);
    if (someFieldsAreFilled) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  return {
    data,
    startSearch: handleSearch,
    isLoading,
    isFetched,
    isError
  };
};
